import { useState } from "react";
import { clsx } from "clsx";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { SectionCard, SubTabs } from "@/features/power_projects/components/ui";
import { useChangeCaretaker, useHandOverForm, useLeadershipAnalysis, useRevokeLeadership } from "../hooks";
import type { LedSummary } from "../types";
import { AnalysisPanel } from "./AnalysisPanel";
import { ErrorLine, HeaderMeta, INPUT_SM, ReasonBox, fmtDate } from "./common";

type Window = "7" | "14" | "30" | "60";

/** „Kontrola" — zakładka tylko dla nas: decyzje dyrekcji nad przekazaniem i rozliczenie
 *  firmy w oknie (ten sam panel, co „Kontrola procesu › Podwykonawcy", zawężony do tego
 *  jednego projektu). Firma tej zakładki nie widzi i nie ma do niej endpointu. */
export function ControlTab({ summary, canDecide }: { summary: LedSummary; canDecide: boolean }) {
  const navigate = useNavigate();
  const [days, setDays] = useState<Window>("14");
  const { data, isLoading, error } = useLeadershipAnalysis(summary.id, Number(days));
  const revoke = useRevokeLeadership(summary.id);
  const change = useChangeCaretaker(summary.id);
  const { data: form } = useHandOverForm(summary.connection_id, canDecide);
  const [pickingCaretaker, setPickingCaretaker] = useState(false);
  const [caretaker, setCaretaker] = useState(summary.internal?.caretaker_id ?? "");
  const [revoking, setRevoking] = useState(false);

  return (
    <div className="space-y-4">
      {/* Nadzór nad przekazaniem — fakty po lewej, decyzje po prawej. Czerwona sztaba,
          bo to jedyne miejsce, z którego można firmie odebrać projekt. */}
      <SectionCard title="Nadzór nad przekazaniem" bodyClassName="p-0">
        <div
          className="flex flex-wrap items-start justify-between gap-4 px-4 py-3"
          style={{ borderLeft: "3px solid var(--color-primary)" }}
        >
          <dl className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
            <HeaderMeta label="Prowadzi" value={summary.contractor_name} />
            <HeaderMeta label="Opiekun ELEKTIM" value={summary.internal?.caretaker_full_name || summary.caretaker_label || "—"} />
            <HeaderMeta label="Przekazano">
              <span className="font-mono text-[13px]">{fmtDate(summary.handed_over_at)}</span>
              {summary.internal?.handed_over_by_label && (
                <span className="block text-[11px] text-[var(--color-text-muted)]">{summary.internal.handed_over_by_label}</span>
              )}
            </HeaderMeta>
            <HeaderMeta label="Zamówienie">
              {summary.order_number ? (
                <Link to={`/orders/${summary.order_id}`} className="font-mono text-[13px] text-[var(--color-primary)] hover:underline">
                  {summary.order_number}
                </Link>
              ) : (
                <span className="text-[13px]">—</span>
              )}
            </HeaderMeta>
          </dl>
          {canDecide && (
            <div className="flex flex-wrap items-center gap-2">
              {pickingCaretaker ? (
                <>
                  <select className={clsx(INPUT_SM, "w-auto")} value={caretaker} onChange={(e) => setCaretaker(e.target.value)}>
                    {(form?.caretakers ?? []).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    disabled={!caretaker || change.isPending}
                    onClick={async () => {
                      await change.mutateAsync(caretaker);
                      setPickingCaretaker(false);
                    }}
                  >
                    Zapisz
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setPickingCaretaker(false)}>Anuluj</Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setPickingCaretaker(true)}>Zmień opiekuna</Button>
                  <Button size="sm" variant="danger" onClick={() => setRevoking(true)}>Odbierz projekt</Button>
                </>
              )}
            </div>
          )}
        </div>
        <ErrorLine error={change.error} className="px-4 pb-2" />
      </SectionCard>

      <SectionCard
        title="Rozliczenie okna"
        action={
          <span className="flex flex-wrap items-center gap-2">
            <SubTabs<Window>
              active={days}
              onChange={setDays}
              tabs={[
                { key: "7", label: "7 dni" },
                { key: "14", label: "14 dni" },
                { key: "30", label: "30 dni" },
                { key: "60", label: "60 dni" },
              ]}
            />
            {data && (
              <span className="section-label">
                {fmtDate(data.window.since)} – {fmtDate(data.window.until)}
              </span>
            )}
          </span>
        }
        bodyClassName="p-4"
      >
        {isLoading ? (
          <div className="flex h-32 items-center justify-center"><Spinner /></div>
        ) : (
          <>
            <ErrorLine error={error} />
            {data && <AnalysisPanel data={data} singleProject />}
          </>
        )}
      </SectionCard>

      <Modal open={revoking} onClose={() => setRevoking(false)} title="Odebrać projekt podwykonawcy?" size="sm">
        <p className="text-[13px]">
          Firma <strong>{summary.contractor_name}</strong> natychmiast straci dostęp, a pełny warsztat wróci. Dane,
          dziennik i zamówienie zostają.
        </p>
        <ReasonBox
          label="Powód odebrania (trafia do dziennika)"
          danger
          confirmLabel="Odbierz projekt"
          busy={revoke.isPending}
          onCancel={() => setRevoking(false)}
          onConfirm={async (reason) => {
            await revoke.mutateAsync(reason);
            navigate(`/power-projects/${summary.project_id}`);
          }}
        />
        <ErrorLine error={revoke.error} />
      </Modal>
    </div>
  );
}
